import { useCallback, useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { PayrollEmployee } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type PayrollRosterStatus = "loading" | "ready" | "error";

export interface UsePayrollRosterResult {
  /**
   * The roster, in employee-number order as the API returned it — never re-sorted here.
   * A payroll run is read top to bottom against last period's, and the order is the
   * server's to decide.
   *
   * `null` until it resolves, and `null` if the fetch failed. Deliberately not `[]` in
   * either case: an empty array is a real, different answer ("this entity has nobody on
   * its payroll"), and collapsing "we do not know yet" into it is what would let the
   * payroll screens fire a run for an empty roster or render a confident $0.00 total for
   * a farm whose roster simply had not arrived.
   */
  employees: PayrollEmployee[] | null;
  status: PayrollRosterStatus;
  error: string | null;
  /** ef-058-4 — re-reads the roster after a People-tab change. Keeps the current rows on
   *  screen while it runs (no flash back to "loading"), so an open table does not unmount. */
  refetch: () => void;
}

/**
 * Fetches the tenant's payroll roster from GET /api/demo/employees.
 *
 * This hook is what closed the largest remaining fake in the product. The Payroll screen
 * computed genuinely real withholding — through the tested EasyFarm.PayrollDeductions
 * engine — for twelve people who existed nowhere but `pages/payroll-fixtures.ts`, on the
 * one screen whose whole claim is that the numbers are real. The people are rows now
 * (`db/migrations/0003_employee.sql`), tenant-scoped by row-level security like everything
 * else this client reads.
 *
 * Shared by BOTH payroll screens — Payroll.tsx and PaidLeave.tsx — because they run the
 * same roster through two different engines. Two fetches of the same list would be two
 * chances to disagree about who is on the payroll.
 *
 * Reference data, so it is fetched once per mount and never polled: a roster does not
 * change mid pay-run, and the same reasoning as useReferenceData applies.
 *
 * A failure is NOT degraded to an empty roster. There is no honest payroll screen without
 * a roster, and an empty one would render as a valid run of nobody — a $0.00 total that
 * looks like an answer. It surfaces as an error the screens report.
 */
/**
 * @param scope Which business's people to return.
 *
 *   "primary"  — the entity employing the most people. What the Payroll, Paid Leave and
 *                Dashboard screens want: those are the FARM's screens, and a tenant can
 *                hold a second, non-farm business whose staff have no place in the farm's
 *                pay run.
 *   "tenant"   — everyone, across every entity. What the year-end screen wants, because
 *                it groups by business itself and offers a switcher.
 *
 * THIS PARAMETER EXISTS BECAUSE ITS ABSENCE WAS A BUG. Until a second entity was seeded
 * there was only ever one roster, so "the tenant's employees" and "the farm's employees"
 * were the same list and nothing distinguished them. The moment Larson Trucking arrived,
 * its four drivers walked into the farm's payroll run: the Payroll screen read "16
 * employees · $34,515" instead of 12 · $18,415, and the Dashboard's payroll tile followed
 * it. Every test stayed green — they seed their own single-entity tenants — and the
 * regenerated visual baselines captured the wrong figure as correct.
 *
 * "Primary" is deliberately derived from the DATA (most employees) rather than a flag on
 * the row: nothing in the schema marks an entity as the main one, and inventing a column
 * to encode it would be asserting something the customer never told us. It is the same
 * rule the year-end screen already uses to pick its default business.
 */
export function usePayrollRoster(scope: "primary" | "tenant" = "primary"): UsePayrollRosterResult {
  const [employees, setEmployees] = useState<PayrollEmployee[] | null>(null);
  const [status, setStatus] = useState<PayrollRosterStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const refetch = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    demoApi
      .getEmployees(controller.signal)
      .then((roster) => {
        if (cancelled) return;
        setEmployees(scope === "tenant" ? roster : onlyPrimaryEntity(roster));
        setStatus("ready");
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setEmployees(null);
        setStatus("error");
        setError(
          describeReadFailure(err),
        );
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [scope, reloads]);

  return { employees, status, error, refetch };
}

/**
 * The roster of whichever entity employs the most people.
 *
 * Ties are broken by the first entity encountered, which is stable because the API
 * returns employees ordered by number. A tie means two businesses of equal size and no
 * data anywhere saying which is the farm — at which point the screens need an entity
 * switcher rather than a better guess, and the year-end screen already has one.
 */
function onlyPrimaryEntity(roster: PayrollEmployee[]): PayrollEmployee[] {
  if (roster.length === 0) return roster;

  const counts = new Map<string, number>();
  for (const employee of roster) {
    counts.set(employee.entityId, (counts.get(employee.entityId) ?? 0) + 1);
  }

  // roster[0] is guaranteed by the length check above, but noUncheckedIndexedAccess does
  // not know that — and asserting past it with ! is exactly how a real empty-array crash
  // gets introduced later when the guard moves.
  const first = roster[0];
  if (first === undefined) return roster;

  let primary = first.entityId;
  for (const [entityId, count] of counts) {
    if (count > (counts.get(primary) ?? 0)) primary = entityId;
  }

  return roster.filter((employee) => employee.entityId === primary);
}
